import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, ChevronDown, X, Users, AudioLines } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import ExpeditionCard from "@/components/site/ExpeditionCard";
import MediaEmbed from "@/components/site/MediaEmbed";
import ContactCTA from "@/components/site/ContactCTA";
import ManifestoCarousel from "@/components/site/ManifestoCarousel";
import JsonLd from "@/components/site/JsonLd";
import { graph, organizationNode, webSiteNode } from "@/lib/schema";
import { useSiteInfo } from "@/lib/siteInfo";
import { useSeo } from "@/lib/seo";
import { Reveal, MaskedLines, Marquee, SafeImg, EASE } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets } from "@/lib/assets";
import api, { resolveMediaUrl } from "@/lib/api";
import { imgSrc, imgSet } from "@/lib/img";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1543894597-a78475d42a11?q=80&w=2000&auto=format&fit=crop";

export default function Home() {
  const { t, lang } = useLang();
  const assets = useAssets();
  const [tours, setTours] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [audio, setAudio] = useState([]);
  const [film, setFilm] = useState(null);
  const [filmOpen, setFilmOpen] = useState(false);
  const [destIdx, setDestIdx] = useState(0);
  const [destinations, setDestinations] = useState([]);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 220]);
  const heroScale = useTransform(scrollY, [0, 900], [1.08, 1.2]);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
    api.get("/leaders").then((r) => setLeaders(r.data.slice(0, 3))).catch(() => {});
    api.get("/media", { params: { type: "audio", limit: 3 } }).then((r) => setAudio(r.data)).catch(() => {});
    api.get("/media", { params: { type: "video", limit: 1 } }).then((r) => setFilm(r.data[0] || null)).catch(() => {});
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filmOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setFilmOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filmOpen]);

  const heroImg = resolveMediaUrl(assets && assets.home_hero) || HERO_FALLBACK;

  useSeo({
    title: t("seo.home.title"),
    description: t("seo.home.desc"),
    path: "/",
    image: heroImg,
    preloadImage: imgSrc(heroImg, 1600),
    preloadSrcSet: imgSet(heroImg, [800, 1200, 1600, 2000]),
  });
  const siteInfo = useSiteInfo();
  const jsonLd = useMemo(() => graph([organizationNode(siteInfo || {}), webSiteNode(lang)]), [siteInfo, lang]);
  const filmMedia = (assets && assets.home_film_url)
    ? { url: assets.home_film_url, media_type: "video", title: "" }
    : ((assets && assets.home_film) || film);

  // Destinazioni e coordinate: derivate dalle spedizioni pubblicate (una sola volta per località), sincronizzate col CMS
  const dests = destinations.map((d) => (d.coordinates ? `${d.name.toUpperCase()} — ${d.coordinates}` : d.name.toUpperCase()));
  useEffect(() => {
    if (!dests.length) return undefined;
    const id = setInterval(() => setDestIdx((i) => (i + 1) % dests.length), 2600);
    return () => clearInterval(id);
  }, [dests.length]);
  const marqueeItems = [...destinations.map((d) => d.name), "De Los Cielos"];

  const manifestoCfg = (assets && assets.manifesto) || [];
  const chapters = [1, 2, 3, 4]
    .map((n) => {
      const cfg = manifestoCfg.find((m) => m.key === n) || {};
      const tr = lang === "it" ? cfg : (cfg.translations || {})[lang] || {};
      return {
        n: `0${n}`,
        key: n,
        visible: cfg.visible !== false,
        title: tr.title || cfg.title || t(`manifesto.${n}.title`),
        text: tr.text || cfg.text || t(`manifesto.${n}.text`),
      };
    })
    .filter((c) => c.visible);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-home" data={jsonLd} />

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[620px] flex items-end overflow-hidden" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <img
            src={imgSrc(heroImg, 1600)}
            srcSet={imgSet(heroImg, [800, 1200, 1600, 2000])}
            sizes="100vw"
            alt="Esploratore sotto un cielo stellato"
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/30 to-obsidian" />
        <div className="absolute inset-0 grain" />

        <div className="container-x relative z-10 pb-20 sm:pb-28">
          <div className="max-w-4xl">
            <motion.p
              className="eyebrow mb-6 flex items-start sm:items-center gap-3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-10 bg-copper inline-block shrink-0 mt-2 sm:mt-0" /> {t("hero.eyebrow")}
            </motion.p>

            <h1 className="font-serif font-semibold text-[13vw] sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight" data-testid="hero-title">
              <MaskedLines
                lines={[[t("hero.line1"), ""], [t("hero.line2"), "italic text-copperlight"]]}
                delay={0.35}
              />
            </h1>

            <motion.div
              className="mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            >
              <Link to="/viaggi" className="btn-copper" data-testid="hero-cta-expeditions">
                {t("hero.cta1")} <ArrowRight className="h-4 w-4" />
              </Link>
              <button onClick={() => setFilmOpen(true)} className="btn-ghost" data-testid="hero-cta-video-modal">
                <Play className="h-4 w-4" /> {t("hero.cta2")}
              </button>
            </motion.div>
          </div>

          <div className="mt-14 flex items-end justify-between">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-copperlight overflow-hidden h-5" data-testid="hero-coordinates">
              <AnimatePresence mode="wait">
                <motion.span key={destIdx} className="block" initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -18, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} data-testid="hero-coordinate-current">
                  {dests.length ? dests[destIdx % dests.length] : ""}
                </motion.span>
              </AnimatePresence>
            </div>
            <div className="hidden sm:flex flex-col items-center gap-2 text-sand">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{t("hero.scroll")}</span>
              <ChevronDown className="h-4 w-4 animate-bounce text-copper" />
            </div>
          </div>
        </div>
      </section>

      {/* FILM MODAL */}
      <AnimatePresence>
        {filmOpen && (
          <motion.div
            className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFilmOpen(false)}
            data-testid="film-modal"
          >
            <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              {filmMedia ? (
                <MediaEmbed media={filmMedia} />
              ) : (
                <div className="aspect-video flex items-center justify-center card-dark text-sand text-sm">Film in lavorazione — torna presto.</div>
              )}
              <div className="flex justify-between items-center mt-4">
                <p className="font-serif text-lg text-cream">{filmMedia ? filmMedia.title : "De Los Cielos"}</p>
                <button className="btn-ghost !px-4 !py-2" onClick={() => setFilmOpen(false)} data-testid="film-modal-close">
                  <X className="h-4 w-4" /> Chiudi
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MARQUEE */}
      <div data-testid="home-marquee">
        <Marquee items={marqueeItems} />
      </div>

      {/* MANIFESTO */}
      <section className="py-24 sm:py-32" id="manifesto" data-testid="manifesto">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-3">{t("manifesto.eyebrow")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream max-w-2xl leading-[1.05]">{t("manifesto.title")}</h2>
          </Reveal>
          <div className="mt-14 sm:mt-20" data-testid="manifesto-grid">
            <ManifestoCarousel chapters={chapters} />
          </div>
        </div>
      </section>

      {/* EXPEDITIONS PREVIEW */}
      <section className="py-24 sm:py-32 border-t border-white/5" data-testid="home-expeditions">
        <div className="container-x">
          <div className="mb-12">
            <Reveal>
              <p className="eyebrow mb-3">{t("home.exped.eyebrow")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-cream">{t("home.exped.title")}</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8" data-testid="expeditions-grid">
            {tours.map((tour, i) => (
              <ExpeditionCard key={tour.id} tour={tour} index={i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-14 pt-10 border-t border-white/5 flex justify-center sm:justify-end">
              <Link to="/viaggi" className="btn-ghost" data-testid="home-exped-link-all">
                {t("home.exped.cta")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUDIO DIARIES */}
      {audio.length > 0 && (
        <section className="py-24 sm:py-32 border-t border-white/5 bg-obsidian2" data-testid="home-audio-section">
          <div className="container-x">
            <Reveal className="mb-12 max-w-xl">
              <p className="eyebrow flex items-center gap-2"><AudioLines className="h-4 w-4" /> {t("home.audio.eyebrow")}</p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {audio.map((m, i) => (
                <Reveal key={m.id} delay={i * 0.1}>
                  <MediaEmbed media={m} onPlay={(id) => api.post(`/media/${id}/play`).catch(() => {})} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LEADERS PREVIEW */}
      <section className="py-24 sm:py-32" data-testid="home-leaders">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <Reveal>
              <p className="eyebrow mb-3">{t("home.leaders.eyebrow")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-cream">{t("home.leaders.title")}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/leader" className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-copperlight flex items-center gap-2" data-testid="home-leaders-link-all">
                {t("home.leaders.cta")} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-8" data-testid="leaders-grid">
            {leaders.map((l0, i) => {
              const l = L(l0, lang);
              return (
              <Reveal key={l0.id} delay={i * 0.1} className="h-full">
                <Link to={`/leader/${l0.slug}`} className="group block" data-testid={`leader-card-${l0.slug}`}>
                  <div className="relative overflow-hidden border border-white/5 group-hover:border-copper/50 transition-colors duration-500">
                    <div className="aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
                      <SafeImg src={(assets && assets.leaders && assets.leaders[l0.slug] && assets.leaders[l0.slug].portrait) || l.portrait} alt={l.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                    </div>
                    <span className="absolute bottom-3 right-3 font-mono text-[10px] text-copperlight/0 group-hover:text-copperlight transition-colors duration-300">→</span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-2xl text-cream mt-3 sm:mt-4 group-hover:text-copperlight transition-colors">{l.name}</h3>
                  <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-sand mt-1">{l.role}</p>
                </Link>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA / FORM */}
      <ContactCTA to="/contatti" bg={assets && assets.cta_bg && assets.cta_bg.home} testId="home-cta" />

      <Footer />
    </div>
  );
}
